'use client'

import { Button } from '@/components/ui/button'
import { ROUTES } from '@/config/navigation'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { API_KEY } from '@/lib/constants'
import {
  GeolocationControl,
  Map,
  Placemark,
  YMaps,
  ZoomControl
} from '@pbe/react-yandex-maps'
import '@yandex/ymaps3-default-ui-theme/dist/esm/index.css'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { BranchSelect } from './branch-select'
import { BranchToggle } from './branch-toggle'
import { BRANCH_DATA } from './config'

const [lat, lon] = [40.209607, 44.530513]

interface IBranchesMapProps {
  title: string
  content: string
  showButton?: boolean
}

export default function BranchesMap({
  title,
  content,
  showButton = true
}: IBranchesMapProps) {
  const [focusedBranch, setFocusedBranch] = useState<string | null>('10')
  const [selectedRegions, setSelectedRegions] = useState<string[] | null>(null)
  const [center, setCenter] = useState([lat, lon])
  const [shouldLoadMap, setShouldLoadMap] = useState(false)
  const mapContainerRef = useRef<HTMLDivElement>(null)

  const isMobile = useIsMobile()

  const t = useTranslations('Home')
  const tRegions = useTranslations('Regions')
  const tBranches = useTranslations('Branches')

  useEffect(() => {
    const refCopy = mapContainerRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !shouldLoadMap) {
          setShouldLoadMap(true)
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px'
      }
    )

    if (refCopy) {
      observer.observe(refCopy)
    }

    return () => {
      if (refCopy) {
        observer.unobserve(refCopy)
      }
    }
  }, [shouldLoadMap])

  const behaviors = useMemo(
    () =>
      isMobile
        ? ['drag', 'mouseTilt', 'multiTouch', 'scrollZoom']
        : ['drag', 'mouseTilt'],
    [isMobile]
  )

  const toggleBranch = useCallback(
    (coordinates: number[], branchId: string) => {
      setCenter(coordinates)

      if (focusedBranch === branchId) {
        setFocusedBranch(null)
        setCenter([lat, lon])
      } else {
        setFocusedBranch(branchId)
      }
    },
    [focusedBranch]
  )

  const onRegionChange = useCallback((regions: string[]) => {
    setSelectedRegions(regions)
  }, [])

  const filteredBranches = useMemo(
    () =>
      BRANCH_DATA.filter((branch) => {
        if (!selectedRegions || selectedRegions.length === 0) return true
        return selectedRegions.includes(tRegions(branch.region.id))
      }),
    [selectedRegions, tRegions]
  )

  const generateBallonBody = useCallback(
    (branch: (typeof BRANCH_DATA)[0]) => {
      return `<div class="flex flex-col gap-4 rounded-2xl bg-white p-4  text-primary-500">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2 text-sm font-semibold">
          <img src="/location.svg" alt="Branch Icon" class="h-4 w-4" />
          <span>${tBranches(branch.city)}</span>
        </div>
        <div class="pl-6 text-sm text-gray-600">
          ${tBranches(branch.street)}
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-start gap-2 text-sm font-semibold">
          <img src="/time.svg" alt="Time Icon" class="h-4 w-4 mt-0.5" />
          <div class="flex flex-col gap-1">
            <span>${tBranches(branch.days.monFry)}: <span class="font-normal text-gray-600">${tBranches(branch.hours.monFry)}</span></span>
            <span>${tBranches(branch.days.satSun)}: <span class="font-normal text-gray-600">${tBranches(branch.hours.satSun)}</span></span>
          </div>
        </div>
      </div>
    </div>
    `
    },
    [tBranches]
  )

  return (
    <div
      id="map"
      className="mx-auto w-full max-w-[1280px] rounded-3xl bg-white py-6 text-center shadow-2xl md:rounded-[48px] md:py-10 md:text-left"
    >
      <div className="flex h-full w-full flex-col items-center justify-between px-4 pt-4 md:gap-4 md:px-8">
        <div className="flex w-full flex-col items-center gap-4 md:flex-row md:justify-between">
          <div className="space-y-2">
            <h3 className="text-heading-5 md:text-heading-4 text-primary-600 mb-2 font-bold">
              {title}
            </h3>
            <p className="text-body-3 md:text-body-2 text-primary-600 mb-2 leading-relaxed">
              {content}
            </p>
          </div>
          {showButton && (
            <Button
              href={ROUTES.BRANCHES}
              label={t('branches.buttonText')}
              variant="primary"
              className="text-primary-600 mt-4 mb-6"
            />
          )}
        </div>

        <div className="w-full flex-row-reverse gap-6 md:flex">
          <div
            ref={mapContainerRef}
            className="mb-4 h-full w-full overflow-hidden rounded-2xl border border-gray-200 md:mb-0"
          >
            {shouldLoadMap ? (
              <YMaps
                query={{
                  apikey: API_KEY,
                  lang: 'en_US',
                  load: 'geoObject.addon.balloon,geoObject.addon.hint'
                }}
              >
                <Map
                  state={{
                    center,
                    zoom: 15,
                    behaviors
                  }}
                  width={'100%'}
                  height={'470px'}
                >
                  {filteredBranches.map((branch) => (
                    <Placemark
                      key={branch.id}
                      onClick={() => setFocusedBranch(branch.id)}
                      geometry={branch.cord}
                      properties={{
                        balloonContentHeader: ``,
                        balloonContentBody: generateBallonBody(branch)
                      }}
                      options={{
                        iconLayout: 'default#image',
                        iconImageHref: '/placemark.png',
                        iconImageSize: [30, 30],
                        hideIconOnBalloonOpen: false,
                        balloonCloseButton: true,
                        balloonOffset: [0, -30]
                      }}
                    />
                  ))}

                  <GeolocationControl />
                  <ZoomControl
                    options={{
                      position: {
                        top: 55,
                        left: 10
                      }
                    }}
                  />
                </Map>
              </YMaps>
            ) : (
              <div className="flex h-[470px] w-full items-center justify-center bg-gray-50">
                <div className="flex flex-col items-center gap-4">
                  <div className="border-secondary-300 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent"></div>
                </div>
              </div>
            )}
          </div>
          <div className="w-full max-w-[380px] space-y-4">
            <BranchSelect onSelectRegions={onRegionChange} />
            <BranchToggle
              onToggleBranch={toggleBranch}
              focusedBranch={focusedBranch}
              filteredBranches={filteredBranches}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
